import { useEffect, useMemo, useRef, useState } from "react";
import {
  ChevronRight,
  CircleDollarSign,
  FileText,
  Info,
  List,
  RotateCcw,
  Truck,
  Weight,
  X,
} from "lucide-react";
import { toast } from "sonner";
import {
  dropshipV2ErrorMessage,
  type DropshipV2ProposedPO,
  useApproveDropshipV2ProposedPO,
  useDropshipV2DeliveryTiming,
  useDropshipV2POPreviewBlob,
  useRejectDropshipV2ProposedPOToSales,
  useUpdateDropshipV2ProposedPO,
  useUpdateDropshipV2ProposedPOLineNote,
} from "@/api/dropship-v2";
import {
  DropshipLineColumnHeaders,
  DropshipLineControlBand,
  DropshipLineDataGrid,
  DropshipLineNumericGrid,
  DropshipLineRowShell,
  DropshipLineValue,
} from "@/components/dropship-line-item-layout";
import { ApprovedFreightPricing } from "@/components/dropship-freight-review";
import { deliveryTiming } from "@/lib/delivery-timing";
import { POInlineEditableField } from "@/components/po-inline-edit";
import { POLineItemProductCell } from "@/components/po-line-item-product-cell";
import { ProductDetailsDialogTrigger } from "@/components/product-details-dialog";
import {
  ReviewApprovalPanel,
  type ReviewCheck,
} from "@/components/review-approval-panel";
import { ReviewChoiceGroup } from "@/components/review-choice-group";
import { ActionPopover } from "@/components/action-popover";
import {
  SupplierRecipientEnvelopeEditor,
  type SupplierRecipientEnvelope,
  type SupplierRecipientMutationCallbacks,
} from "@/components/supplier-recipient-envelope-editor";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { cn, formatMoney, formatStatus } from "@/lib/utils";

type DecisionState = "unknown" | "true" | "false";
type SpecCreditDecision =
  "normal_request" | "already_secured" | "not_applicable";
type ProposedPOBlocker = NonNullable<
  NonNullable<DropshipV2ProposedPO["review"]>["blockers"]
>[number];

const DECISION_OPTIONS = [
  { value: "unknown", label: "Unknown" },
  { value: "true", label: "Required" },
  { value: "false", label: "Not required" },
] satisfies ReadonlyArray<{ value: DecisionState; label: string }>;

const DESTINATION_TYPE_OPTIONS = [
  { value: "unknown", label: "Unknown" },
  { value: "true", label: "Residential" },
  { value: "false", label: "Commercial" },
] satisfies ReadonlyArray<{ value: DecisionState; label: string }>;

const SPEC_OPTIONS = [
  { value: "normal_request", label: "Standard request" },
  { value: "already_secured", label: "Already secured" },
  { value: "not_applicable", label: "Not applicable" },
] satisfies ReadonlyArray<{ value: SpecCreditDecision; label: string }>;

const ACCESSORIALS = [
  ["limited_access_delivery", "Limited access"],
  ["inside_delivery", "Inside delivery"],
  ["residential_delivery", "Destination type"],
  ["appointment_delivery", "Appointment"],
  ["construction_site_delivery", "Construction site"],
] as const;

const FREIGHT_OPTIONS = [
  { value: "usars", label: "USA-RS" },
  { value: "manufacturer", label: "Manufacturer" },
] as const;

function text(value: unknown): string {
  if (value == null) return "";
  if (typeof value === "string" || typeof value === "number")
    return String(value);
  return "";
}

function record(value: unknown): Record<string, unknown> {
  return value && typeof value === "object" && !Array.isArray(value)
    ? (value as Record<string, unknown>)
    : {};
}

function recipientEnvelope(value: unknown): SupplierRecipientEnvelope {
  const recipient = record(value);
  return {
    email: text(recipient.email) || null,
    name: text(recipient.name) || null,
    cc_emails: Array.isArray(recipient.cc_emails)
      ? recipient.cc_emails.filter(
          (email): email is string => typeof email === "string",
        )
      : [],
    source: text(recipient.source) || "missing",
  };
}

function specCreditPolicyMessage(policy: Record<string, unknown>): string {
  const reason = text(policy.reason);
  if (reason === "manufacturer_skip_spec_credit") {
    return "This manufacturer is configured not to receive specification-credit requests.";
  }
  if (reason === "below_threshold") {
    return `The merchandise net is below the ${formatMoney(text(policy.effective_minimum))} request minimum.`;
  }
  if (reason === "skip_recipient") {
    return "The manufacturer’s ordering contact does not receive specification-credit requests.";
  }
  return "Manufacturer policy does not allow a standard specification-credit request.";
}

function money(value: unknown): string {
  const stringValue = text(value);
  return stringValue ? formatMoney(stringValue) : "—";
}

function lineMoney(value: string | null | undefined): string {
  return value == null || value === "" ? "—" : formatMoney(value);
}

function positiveMoney(value: unknown): boolean {
  const amount = Number(text(value));
  return Number.isFinite(amount) && amount > 0;
}

function accessorialValue(
  po: DropshipV2ProposedPO,
  key: string,
): DecisionState {
  const raw = po.accessorial_decisions[key];
  const value =
    raw && typeof raw === "object" && "value" in raw ? raw.value : raw;
  if (value === true) return "true";
  if (value === false) return "false";
  return "unknown";
}

function scrollToId(id: string) {
  document
    .getElementById(id)
    ?.scrollIntoView({ behavior: "smooth", block: "center" });
}

function scrollToAndFocusId(id: string) {
  const target = document.getElementById(id);
  target?.scrollIntoView({ behavior: "smooth", block: "center" });
  target?.focus({ preventScroll: true });
}

function blockerAction(
  proposedPO: DropshipV2ProposedPO,
  blocker: ProposedPOBlocker,
) {
  const fields = [blocker.field, ...(blocker.fields ?? [])].filter(
    (field): field is string => typeof field === "string",
  );
  const accessorialFields: readonly string[] = [
    "liftgate_delivery",
    ...ACCESSORIALS.map(([key]) => key),
  ];
  if (
    blocker.code.includes("recipient") ||
    fields.some((field) => field.includes("recipient"))
  ) {
    return {
      actionLabel: "Review supplier emails",
      onClick: () =>
        scrollToAndFocusId(`po-supplier-recipients-${proposedPO.uuid}`),
    };
  }
  if (
    blocker.code.includes("accessorial") ||
    blocker.code.includes("freight") ||
    fields.some((field) => accessorialFields.includes(field))
  ) {
    return {
      actionLabel: "Review delivery settings",
      onClick: () => scrollToId(`po-accessorials-${proposedPO.uuid}`),
    };
  }
  if (blocker.code.includes("spec_credit")) {
    return {
      actionLabel: "Review specification credit",
      onClick: () => scrollToId(`po-spec-credit-${proposedPO.uuid}`),
    };
  }
  if (
    blocker.code === "manufacturer_unresolved" ||
    blocker.code === "po_has_no_lines"
  ) {
    return {
      actionLabel: "Review PO lines",
      onClick: () => scrollToId(`po-lines-${proposedPO.uuid}`),
    };
  }
  return {
    actionLabel:
      blocker.resolution_owner === "sales"
        ? "Reopen the quote to fix this"
        : "Review required change",
    onClick: () => scrollToId(`proposed-po-${proposedPO.uuid}`),
  };
}

function DecisionField({
  label,
  value,
  pending,
  disabled = false,
  placeholder = "-",
  kind = "text",
  className,
  onEditingChange,
  onSave,
}: {
  label: string;
  value: string;
  pending: boolean;
  disabled?: boolean;
  placeholder?: string;
  kind?: "text" | "textarea";
  className?: string;
  onEditingChange?: (editing: boolean) => void;
  onSave: (value: string) => void;
}) {
  return (
    <div className={cn("min-w-0", className)}>
      <span className="block text-xs font-medium text-muted-foreground">
        {label}
      </span>
      <POInlineEditableField
        value={value}
        displayValue={value}
        placeholder={placeholder}
        kind={kind}
        disabled={disabled}
        pending={pending}
        showEditIcon
        ariaLabel={`Edit ${label}`}
        buttonClassName="max-w-full px-1 text-left text-sm"
        inputClassName={kind === "textarea" ? "min-w-64" : "min-w-36"}
        onEditingChange={onEditingChange}
        onConfirm={(nextValue) => onSave(nextValue.trim())}
      />
    </div>
  );
}

function PriceValue({
  label,
  value,
  emphasized = false,
}: {
  label: string;
  value: string;
  emphasized?: boolean;
}) {
  return (
    <DropshipLineValue
      label={label}
      mode="po"
      className={emphasized ? "font-semibold text-foreground" : undefined}
    >
      <span
        className={cn(
          "flex min-h-11 items-center justify-end px-1 text-sm tabular-nums @2xl/dropship-lines:min-h-8 @2xl/dropship-lines:text-xs",
          emphasized && "font-semibold text-foreground",
        )}
      >
        {value}
      </span>
    </DropshipLineValue>
  );
}

export function DropshipProposedPOLineTable({
  draftUuid,
  proposedPO,
  titleId = "proposed-po-lines-title",
  disabled = false,
  framed = true,
  onEditingChange,
}: {
  draftUuid: string;
  proposedPO: DropshipV2ProposedPO;
  titleId?: string;
  disabled?: boolean;
  framed?: boolean;
  onEditingChange?: (field: string, editing: boolean) => void;
}) {
  const lines = proposedPO.lines;
  const noteUpdate = useUpdateDropshipV2ProposedPOLineNote(draftUuid);
  return (
    <section
      aria-labelledby={titleId}
      className={cn(
        "@container/dropship-lines overflow-hidden",
        framed && "rounded-xl border",
      )}
    >
      {framed ? (
        <div className="flex items-center gap-2 border-b bg-muted/20 px-4 py-3">
          <h4 id={titleId} className="text-sm font-semibold">
            PO line items
          </h4>
          <Badge variant="outline">{lines.length}</Badge>
        </div>
      ) : (
        <h4 id={titleId} className="sr-only">
          PO line items
        </h4>
      )}
      <DropshipLineColumnHeaders mode="po" />
      {lines.map((line) => {
        const child = line.parent_draft_line_id != null;
        const selectedConfiguration =
          line.line_kind === "configuration" &&
          line.pricing_source === "config_accessory_v4_explicit_zero";
        return (
          <DropshipLineRowShell
            key={line.uuid}
            child={child}
            data-dropship-equipment-row={child ? "child" : "parent"}
          >
            <DropshipLineControlBand child={child} mode="po">
              <div
                className={cn(
                  "flex min-w-0 flex-1 flex-wrap items-center gap-1.5 @2xl/dropship-lines:gap-2",
                  child && "pl-3",
                )}
              >
                {child ? (
                  <ChevronRight
                    className="size-3.5 text-muted-foreground"
                    aria-hidden="true"
                  />
                ) : null}
                <span className="text-xs font-semibold text-muted-foreground">
                  {line.display_number}
                </span>
                {child ? (
                  <Badge variant="outline" className="font-normal">
                    {selectedConfiguration
                      ? "Selected configuration"
                      : formatStatus(line.line_kind)}
                  </Badge>
                ) : null}
                {line.product_id == null && !selectedConfiguration ? (
                  <Badge variant="secondary">
                    {line.custom_item ? "Custom" : "Unmatched"}
                  </Badge>
                ) : null}
              </div>
            </DropshipLineControlBand>

            <DropshipLineDataGrid mode="po">
              <div className={cn("min-w-0", child && "pl-4")}>
                <POLineItemProductCell
                  model={line.model}
                  description={line.description}
                  sku={line.sku}
                  productUuid={line.product_uuid}
                  imageUrl={line.product_image_url}
                  manufacturerName={line.manufacturer_name}
                  showManufacturer={false}
                  compact
                  inlineSku
                  className="items-start gap-2"
                  descriptionContent={
                    line.product_uuid && line.description ? (
                      <ProductDetailsDialogTrigger
                        productUuid={line.product_uuid}
                        model={line.model}
                        description={line.description}
                      />
                    ) : undefined
                  }
                />
                <div
                  data-internal-note
                  className={cn(
                    "mt-2 min-w-0",
                    line.internal_note &&
                      "rounded-md border border-amber-200 bg-amber-50/60 px-2 py-1.5 dark:border-amber-800 dark:bg-amber-950/20",
                  )}
                >
                  {line.internal_note ? (
                    <span className="block text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">
                      Internal note
                    </span>
                  ) : null}
                  <POInlineEditableField
                    value={line.internal_note}
                    displayValue={line.internal_note ?? ""}
                    placeholder="-"
                    kind="textarea"
                    allowWrap
                    showEditIcon
                    disabled={disabled}
                    pending={
                      noteUpdate.isPending &&
                      noteUpdate.variables?.line.uuid === line.uuid
                    }
                    ariaLabel={`Edit PO line ${line.display_number} internal note`}
                    buttonClassName={cn(
                      "min-h-8 justify-start px-0 py-0 text-left text-xs leading-5",
                      line.internal_note
                        ? "w-full"
                        : "w-auto text-muted-foreground",
                    )}
                    inputClassName="min-h-24 w-full min-w-64 whitespace-pre-wrap"
                    onEditingChange={(editing) =>
                      onEditingChange?.(`line-note-${line.uuid}`, editing)
                    }
                    onConfirm={(value) =>
                      noteUpdate.mutate({
                        proposedPO,
                        line,
                        internalNote: value.trim() || null,
                      })
                    }
                  />
                </div>
              </div>
              <DropshipLineNumericGrid mode="po">
                <PriceValue label="QTY" value={String(line.quantity)} />
                <PriceValue
                  label="NET"
                  value={lineMoney(line.net_price)}
                  emphasized
                />
                <PriceValue
                  label="DISCOUNT"
                  value={line.discount_chain?.trim() || "—"}
                />
                <PriceValue
                  label="SUPPLIER TOTAL"
                  value={lineMoney(line.line_total)}
                  emphasized
                />
              </DropshipLineNumericGrid>
            </DropshipLineDataGrid>
          </DropshipLineRowShell>
        );
      })}
    </section>
  );
}

function SupplierInstructionsPanel({
  proposedPO,
  pending,
  disabled,
  onSavePOInstructions,
  onSaveShippingInstructions,
  onEditingChange,
}: {
  proposedPO: DropshipV2ProposedPO;
  pending: boolean;
  disabled: boolean;
  onSavePOInstructions: (value: string) => void;
  onSaveShippingInstructions: (value: string) => void;
  onEditingChange: (field: string, editing: boolean) => void;
}) {
  const shippingInstructions = text(
    proposedPO.accessorial_decisions.instructions,
  );
  return (
    <section
      id={`po-instructions-${proposedPO.uuid}`}
      aria-labelledby={`po-instructions-title-${proposedPO.uuid}`}
      className="scroll-mt-6 rounded-xl border border-blue-200 bg-blue-50/40 p-4 dark:border-blue-900 dark:bg-blue-950/20"
    >
      <div className="mb-3">
        <h4
          id={`po-instructions-title-${proposedPO.uuid}`}
          className="text-sm font-semibold"
        >
          Supplier instructions
        </h4>
      </div>
      <div className="grid gap-4 md:grid-cols-2">
        <div className="min-w-0">
          <span className="block text-xs font-semibold text-foreground">
            PO instructions
          </span>
          <p className="mb-1 text-xs text-foreground">
            Ordering or factory-specific direction
          </p>
          <POInlineEditableField
            value={proposedPO.po_instructions}
            displayValue={proposedPO.po_instructions ?? ""}
            placeholder="Add PO instructions"
            kind="textarea"
            allowWrap
            showEditIcon
            disabled={disabled}
            pending={pending}
            ariaLabel="Edit PO instructions"
            buttonClassName="min-h-11 w-full justify-start border border-dashed bg-background px-3 py-2 text-left text-sm leading-5"
            inputClassName="min-h-28 w-full min-w-0"
            onEditingChange={(editing) =>
              onEditingChange("po-instructions", editing)
            }
            onConfirm={onSavePOInstructions}
          />
        </div>
        <div className="min-w-0">
          <span className="block text-xs font-semibold text-foreground">
            Shipping instructions
          </span>
          <p className="mb-1 text-xs text-foreground">
            Delivery contact and approved accessorials are added automatically
          </p>
          <POInlineEditableField
            value={shippingInstructions}
            displayValue={shippingInstructions}
            placeholder="Add shipping instructions"
            kind="textarea"
            allowWrap
            showEditIcon
            disabled={disabled}
            pending={pending}
            ariaLabel="Edit shipping instructions"
            buttonClassName="min-h-11 w-full justify-start border border-dashed bg-background px-3 py-2 text-left text-sm leading-5"
            inputClassName="min-h-28 w-full min-w-0"
            onEditingChange={(editing) =>
              onEditingChange("shipping-instructions", editing)
            }
            onConfirm={onSaveShippingInstructions}
          />
        </div>
      </div>
    </section>
  );
}

function SupplierPDFPreview({
  draftUuid,
  proposedPO,
  enabled,
}: {
  draftUuid: string;
  proposedPO: DropshipV2ProposedPO;
  enabled: boolean;
}) {
  const preview = useDropshipV2POPreviewBlob(
    draftUuid,
    proposedPO.uuid,
    enabled,
  );
  const previewUrl = useMemo(
    () => (preview.data ? URL.createObjectURL(preview.data) : null),
    [preview.data],
  );

  useEffect(
    () => () => {
      if (previewUrl) URL.revokeObjectURL(previewUrl);
    },
    [previewUrl],
  );

  if (preview.isError) {
    return (
      <div className="flex min-h-[34rem] flex-col items-center justify-center gap-3 bg-muted/10 px-6 text-center">
        <FileText className="size-8 text-muted-foreground" aria-hidden="true" />
        <div>
          <p className="text-sm font-semibold">
            Supplier PDF could not be rendered
          </p>
          <p className="mt-1 text-sm text-muted-foreground">
            {dropshipV2ErrorMessage(
              preview.error,
              "Try loading the PDF again.",
            )}
          </p>
        </div>
        <Button
          type="button"
          variant="outline"
          size="sm"
          onClick={() => void preview.refetch()}
        >
          <RotateCcw className="size-4" />
          Try again
        </Button>
      </div>
    );
  }

  if (!previewUrl) {
    return (
      <div
        className="min-h-[34rem] animate-pulse bg-muted/20"
        role="status"
        aria-label="Rendering supplier PDF"
      />
    );
  }

  return (
    <iframe
      key={previewUrl}
      title={`${proposedPO.manufacturer_name} supplier purchase order PDF`}
      src={previewUrl}
      className="h-[calc(100dvh-14rem)] min-h-[40rem] w-full bg-background"
    />
  );
}

/** Shared display-first PO review surface for one generated dropship PO. */
export function DropshipPOReviewCard({
  draftUuid,
  proposedPO,
  canApprove,
  onApproved,
  onEditingStateChange,
}: {
  draftUuid: string;
  proposedPO: DropshipV2ProposedPO;
  canApprove: boolean;
  onApproved?: () => void;
  onEditingStateChange?: (editing: boolean) => void;
}) {
  const update = useUpdateDropshipV2ProposedPO(draftUuid);
  const deliveryTimingQuery = useDropshipV2DeliveryTiming(draftUuid);
  const approve = useApproveDropshipV2ProposedPO(draftUuid);
  const rejectToSales = useRejectDropshipV2ProposedPOToSales(draftUuid);
  const [editingFields, setEditingFields] = useState<Set<string>>(
    () => new Set(),
  );
  const editingStateCallback = useRef(onEditingStateChange);
  const [approvalNote, setApprovalNote] = useState("");
  const [artifactView, setArtifactView] = useState<"lines" | "pdf">("lines");
  const [overrideDialogOpen, setOverrideDialogOpen] = useState(false);
  const trackEditing = (field: string, editing: boolean) => {
    setEditingFields((current) => {
      const next = new Set(current);
      if (editing) next.add(field);
      else next.delete(field);
      return next;
    });
  };
  const hasEditingFields = editingFields.size > 0;
  const recipientEditing =
    editingFields.has("purchase-order-recipient") ||
    editingFields.has("spec-credit-recipient");

  useEffect(() => {
    editingStateCallback.current = onEditingStateChange;
  }, [onEditingStateChange]);

  useEffect(() => {
    editingStateCallback.current?.(hasEditingFields);
  }, [hasEditingFields]);

  useEffect(
    () => () => {
      editingStateCallback.current?.(false);
    },
    [],
  );
  const spec = proposedPO.spec_credit_decision;
  const specDecision = text(spec.decision) as SpecCreditDecision;
  const specPolicy = record(spec.policy);
  const recipients = record(spec.recipients);
  const purchaseOrderRecipient = recipientEnvelope(recipients.purchase_order);
  const specCreditRecipient = recipientEnvelope(recipients.spec_credit);
  const standardRequestAllowed = specPolicy.request_allowed !== false;
  const specOptions = standardRequestAllowed
    ? SPEC_OPTIONS
    : SPEC_OPTIONS.filter((option) => option.value !== "normal_request");
  const needsAccessorialAnswers = Boolean(
    proposedPO.freight_rule_snapshot.we_book_freight,
  );
  const hasMinimumOrderCharge = positiveMoney(proposedPO.minimum_order_charge);
  const hasDropshipFee = positiveMoney(proposedPO.dropship_fee);
  // Policy freight staff took off in review, e.g. free local delivery approved (USA-663).
  const removedFreight = record(proposedPO.freight_rule_snapshot.removed_freight);
  const freightRemoved = text(removedFreight.amount) !== "";
  const hasPolicyFreight =
    positiveMoney(proposedPO.freight_surcharge) || freightRemoved;

  const changeAccessorial = (
    changes: Record<string, unknown>,
    reason: string,
  ) =>
    update.mutate({
      proposedPO,
      changes: { accessorial_decisions: changes, change_reason: reason },
    });
  const changeSpec = (changes: Record<string, unknown>) =>
    update.mutate({
      proposedPO,
      changes: {
        spec_credit_decision: { ...spec, ...changes },
        change_reason: "Spec-credit review updated",
      },
    });
  const changeRecipient = (
    kind: "purchase_order" | "spec_credit",
    envelope: SupplierRecipientEnvelope,
    callbacks: SupplierRecipientMutationCallbacks,
  ) =>
    update.mutate(
      {
        proposedPO,
        changes: {
          spec_credit_decision: {
            ...spec,
            recipients: { ...recipients, [kind]: envelope },
          },
          change_reason: `${kind === "purchase_order" ? "Purchase-order" : "Spec-credit"} recipients updated`,
        },
      },
      {
        onSuccess: callbacks.onSuccess,
        onError: (error) =>
          callbacks.onError(
            new Error(
              dropshipV2ErrorMessage(
                error,
                "Recipients were not saved. Review the addresses and try again.",
              ),
            ),
          ),
      },
    );

  const accessorialComplete =
    !needsAccessorialAnswers ||
    (accessorialValue(proposedPO, "liftgate_delivery") !== "unknown" &&
      ACCESSORIALS.every(
        ([key]) => accessorialValue(proposedPO, key) !== "unknown",
      ));
  const specComplete = specOptions.some(
    (option) => option.value === specDecision,
  );
  const linesComplete =
    proposedPO.lines.length > 0 && Boolean(proposedPO.manufacturer_id);
  // Nothing ships on this PO: a third-party warranty or a manufacturer that ships no products (USA-1100).
  const noFreight = proposedPO.freight_rule_snapshot.no_freight === true;
  // Ordered online or by phone: the PO is never emailed, so its recipient is optional.
  const poEmailSkipped = Boolean(proposedPO.review?.po_email_skipped);
  const recipientBlockers: string[] = [];
  const addRecipientBlockers = (
    label: "purchase order" | "spec credit",
    envelope: SupplierRecipientEnvelope,
    required = true,
  ) => {
    if (!envelope.email?.trim()) {
      if (required) recipientBlockers.push(`Add a ${label} recipient`);
    } else if (!SupplierRecipientEnvelopeEditor.isValidEmail(envelope.email)) {
      recipientBlockers.push(`Fix the ${label} recipient`);
    }
    const invalidCC = envelope.cc_emails.filter(
      (email) => !SupplierRecipientEnvelopeEditor.isValidEmail(email),
    );
    if (invalidCC.length) {
      recipientBlockers.push(
        `Fix ${label} CC ${invalidCC.length === 1 ? "address" : "addresses"}: ${invalidCC.join(", ")}`,
      );
    }
  };
  addRecipientBlockers(
    "purchase order",
    purchaseOrderRecipient,
    !poEmailSkipped,
  );
  if (specDecision === "normal_request") {
    addRecipientBlockers("spec credit", specCreditRecipient);
  }
  const recipientsComplete = recipientBlockers.length === 0;
  const alreadyFinal = ["approved", "placed"].includes(proposedPO.state);
  const serverBlockers = proposedPO.review?.blockers ?? [];
  const serverBlockerCodes = new Set(
    serverBlockers.map((blocker) => blocker.code),
  );
  const localChecks: Array<
    ReviewCheck & { overlapsServerCodes?: readonly string[] }
  > = [
    {
      id: "lines",
      label: "PO lines and hierarchy",
      detail: linesComplete
        ? `${proposedPO.lines.length} ordered line${proposedPO.lines.length === 1 ? "" : "s"} under ${proposedPO.manufacturer_name}`
        : "A canonical manufacturer and at least one line are required",
      state: linesComplete ? "complete" : "blocking",
      onClick: () => scrollToId(`po-lines-${proposedPO.uuid}`),
      overlapsServerCodes: ["manufacturer_unresolved", "po_has_no_lines"],
    },
    {
      id: "supplier-recipients",
      label: "Supplier emails",
      detail: recipientsComplete
        ? poEmailSkipped && !purchaseOrderRecipient.email?.trim()
          ? "Ordered online; no PO email is sent"
          : "Recipients are ready"
        : recipientBlockers.join(". "),
      state: recipientsComplete ? "complete" : "blocking",
      actionLabel: recipientsComplete ? undefined : "Review recipients",
      onClick: () =>
        scrollToAndFocusId(`po-supplier-recipients-${proposedPO.uuid}`),
      overlapsServerCodes: [
        "purchase_order_recipient_invalid",
        "purchase_order_recipient_missing",
        "spec_credit_recipient_invalid",
        "spec_credit_recipient_missing",
      ],
    },
    {
      id: "accessorials",
      label: "Freight and accessorials",
      detail: accessorialComplete
        ? "Per-PO decisions are complete"
        : "Answer each required freight decision",
      state: accessorialComplete ? "complete" : "blocking",
      onClick: () => scrollToId(`po-accessorials-${proposedPO.uuid}`),
      overlapsServerCodes: ["accessorial_unknown"],
    },
    {
      id: "spec-credit",
      label: "Specification credit",
      detail: specComplete
        ? formatStatus(specDecision)
        : "Complete the selected spec-credit decision",
      state: specComplete ? "complete" : "blocking",
      onClick: () => scrollToId(`po-spec-credit-${proposedPO.uuid}`),
      overlapsServerCodes: [
        "spec_credit_decision_missing",
        "spec_credit_policy_conflict",
      ],
    },
  ];
  // The customer's deliver-by date against this PO's arrival if ordered today (USA-1329).
  const arrival = deliveryTimingQuery.data?.arrivals?.find(
    (item) => item.manufacturer_id === proposedPO.manufacturer_id,
  );
  const deliverBy = deliveryTimingQuery.data?.deliver_by;
  const timing =
    deliverBy && !noFreight
      ? deliveryTiming(deliverBy, arrival?.estimated_delivery_date)
      : null;
  if (timing) {
    localChecks.push({
      id: "delivery-timing",
      label: "Delivery timing",
      detail: timing.text,
      state: timing.late ? "warning" : "complete",
    });
  }
  const clientChecks = localChecks.filter(
    (check) =>
      !(noFreight && check.id === "accessorials") &&
      (check.state !== "blocking" ||
        !check.overlapsServerCodes?.some((code) =>
          serverBlockerCodes.has(code),
        )),
  );
  const hasSalesDivergenceBlocker = serverBlockers.some(
    (blocker) =>
      blocker.code === "sales_freight_requote_required" &&
      blocker.override_allowed &&
      blocker.resolution_actions.includes("return_to_sales"),
  );
  const serverChecks: ReviewCheck[] = serverBlockers.map((blocker) => {
    const salesPricingChanged =
      blocker.code === "sales_freight_requote_required";
    return {
      id: `server:${blocker.code}:${blocker.field ?? blocker.fields?.join(",") ?? blocker.message}`,
      label: salesPricingChanged
        ? "Sales pricing is out of date"
        : blocker.resolution_owner === "sales"
          ? "Sales review needed"
          : formatStatus(blocker.code),
      detail: salesPricingChanged
        ? proposedPO.change_reason?.trim() || blocker.message
        : blocker.message,
      state: "blocking",
      ...(salesPricingChanged ? {} : blockerAction(proposedPO, blocker)),
    };
  });
  const reviewRefreshCheck: ReviewCheck[] =
    proposedPO.review || alreadyFinal
      ? []
      : [
          {
            id: "review-refreshing",
            label: "Approval readiness is refreshing",
            detail: "Wait for the latest PO review before approving this PO.",
            state: "blocking",
          },
        ];
  const stateExplanation: ReviewCheck[] =
    proposedPO.state === "needs_changes" || proposedPO.change_reason
      ? [
          {
            id: "po-change-state",
            label:
              proposedPO.state === "needs_changes"
                ? "Changes requested"
                : "PO was revised",
            detail:
              proposedPO.change_reason?.trim() ||
              "This PO is awaiting the requested changes.",
            state: "warning",
          },
        ]
      : [];
  const checks: ReviewCheck[] = hasSalesDivergenceBlocker
    ? [
        ...serverChecks,
        ...reviewRefreshCheck,
        ...clientChecks.filter((check) => check.state !== "complete"),
      ]
    : [
        ...serverChecks,
        ...reviewRefreshCheck,
        ...stateExplanation,
        ...clientChecks,
      ];
  const blockingCount = checks.filter(
    (check) => check.state === "blocking",
  ).length;
  const clientBlockingCount = clientChecks.filter(
    (check) => check.state === "blocking",
  ).length;
  const editsDisabled = proposedPO.state === "placed";
  const approveDisabled =
    !canApprove ||
    proposedPO.review?.can_approve !== true ||
    blockingCount > 0 ||
    hasEditingFields ||
    update.isPending ||
    approve.isPending ||
    rejectToSales.isPending ||
    alreadyFinal ||
    proposedPO.state === "held";
  const overrideDisabled =
    !canApprove ||
    proposedPO.review?.can_override !== true ||
    clientBlockingCount > 0 ||
    hasEditingFields ||
    update.isPending ||
    approve.isPending ||
    rejectToSales.isPending ||
    alreadyFinal;

  return (
    <div
      id={`proposed-po-${proposedPO.uuid}`}
      data-testid="proposed-po-review-card"
      className="grid items-start gap-5 xl:grid-cols-[minmax(0,1fr)_300px]"
    >
      <div className="min-w-0 space-y-5">
        <section
          id={`po-supplier-recipients-${proposedPO.uuid}`}
          tabIndex={-1}
          aria-labelledby={`po-supplier-recipients-title-${proposedPO.uuid}`}
          className="scroll-mt-6 overflow-hidden rounded-xl border bg-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring/50"
        >
          <div className="border-b px-4 py-3">
            <h3
              id={`po-supplier-recipients-title-${proposedPO.uuid}`}
              className="text-sm font-semibold"
            >
              Supplier emails
            </h3>
            {poEmailSkipped ? (
              <p className="mt-1 text-sm text-muted-foreground">
                {proposedPO.manufacturer_name} is ordered online or by phone, so
                this PO is not emailed. Operations gets an action item to place
                it.
              </p>
            ) : null}
          </div>
          <div className="grid divide-y lg:grid-cols-2 lg:divide-x lg:divide-y-0">
            <SupplierRecipientEnvelopeEditor
              id={`purchase-order-recipient-${proposedPO.uuid}`}
              title="Purchase order"
              envelope={purchaseOrderRecipient}
              disabled={editsDisabled}
              pending={update.isPending}
              onEditingChange={(editing) =>
                trackEditing("purchase-order-recipient", editing)
              }
              onSave={(envelope, callbacks) =>
                changeRecipient("purchase_order", envelope, callbacks)
              }
            />
            {specDecision === "normal_request" ? (
              <SupplierRecipientEnvelopeEditor
                id={`spec-credit-recipient-${proposedPO.uuid}`}
                title="Spec credit"
                envelope={specCreditRecipient}
                disabled={editsDisabled}
                pending={update.isPending}
                onEditingChange={(editing) =>
                  trackEditing("spec-credit-recipient", editing)
                }
                onSave={(envelope, callbacks) =>
                  changeRecipient("spec_credit", envelope, callbacks)
                }
              />
            ) : null}
          </div>
        </section>

        <section
          id={`po-accessorials-${proposedPO.uuid}`}
          className="scroll-mt-6 rounded-xl border bg-muted/10 p-4"
        >
          {/* Nothing ships on this PO (e.g. Safeware warranties): no freight to review. */}
          {noFreight ? null : (
            <>
              <div className="mb-3 flex flex-wrap items-center gap-2">
                <Truck
                  className="size-4 text-muted-foreground"
                  aria-hidden="true"
                />
                <h3 className="text-sm font-semibold">
                  Freight and accessorials
                </h3>
              </div>
              <ApprovedFreightPricing
                snapshot={proposedPO.freight_pricing_snapshot}
                className="rounded-lg border bg-background p-3"
              />
            </>
          )}
          {hasPolicyFreight || hasMinimumOrderCharge || hasDropshipFee ? (
            <dl className="mt-2 flex flex-wrap items-center justify-end gap-x-6 gap-y-2 px-3 py-1 text-xs">
              {hasPolicyFreight ? (
                <div className="flex items-center gap-2">
                  <dt className="text-muted-foreground">Freight</dt>
                  <dd className="flex items-baseline gap-1.5 font-semibold tabular-nums">
                    {freightRemoved ? (
                      <>
                        <s className="font-normal text-muted-foreground">
                          {money(removedFreight.amount)}
                        </s>
                        <span>{money(proposedPO.freight_surcharge)}</span>
                      </>
                    ) : (
                      money(proposedPO.freight_surcharge)
                    )}
                  </dd>
                  {editsDisabled ? null : freightRemoved ? (
                    <ActionPopover
                      key="put-freight-back"
                      trigger={
                        <Button
                          type="button"
                          variant="ghost"
                          size="icon"
                          className="size-6 text-muted-foreground"
                          aria-label="Why freight was removed"
                        >
                          <Info className="size-3.5" aria-hidden="true" />
                        </Button>
                      }
                      title="Freight removed"
                      align="end"
                      confirmLabel="Put freight back"
                      confirmVariant="outline"
                      pending={update.isPending}
                      pendingLabel="Putting freight back..."
                      onConfirm={(_note, close) =>
                        update.mutate(
                          {
                            proposedPO,
                            changes: {
                              freight_removed: false,
                              change_reason: "Freight put back",
                            },
                          },
                          { onSuccess: close },
                        )
                      }
                    >
                      <p className="text-sm">{text(removedFreight.reason)}</p>
                    </ActionPopover>
                  ) : (
                    <ActionPopover
                      key="remove-freight"
                      trigger={
                        <Button
                          type="button"
                          variant="ghost"
                          size="icon"
                          className="size-6 text-muted-foreground"
                          aria-label="Remove freight"
                          disabled={update.isPending}
                        >
                          <X className="size-3.5" aria-hidden="true" />
                        </Button>
                      }
                      title="Remove freight from this PO?"
                      align="end"
                      note={{
                        label: "Why does the freight come off?",
                        placeholder: "Example: Free local delivery approved.",
                        minLength: 1,
                      }}
                      confirmLabel="Remove freight"
                      pending={update.isPending}
                      pendingLabel="Removing freight..."
                      onConfirm={(reason, close) =>
                        update.mutate(
                          {
                            proposedPO,
                            changes: {
                              freight_removed: true,
                              change_reason: reason.trim(),
                            },
                          },
                          { onSuccess: close },
                        )
                      }
                    >
                      <p className="text-xs text-muted-foreground">
                        The FREIGHT line comes off the supplier PDF and the PO
                        total.
                      </p>
                    </ActionPopover>
                  )}
                </div>
              ) : null}
              {hasMinimumOrderCharge ? (
                <div className="flex items-baseline gap-2">
                  <dt className="text-muted-foreground">Minimum order</dt>
                  <dd className="font-semibold tabular-nums">
                    {money(proposedPO.minimum_order_charge)}
                  </dd>
                </div>
              ) : null}
              {hasDropshipFee ? (
                <div className="flex items-baseline gap-2">
                  <dt className="text-muted-foreground">Dropship fee</dt>
                  <dd className="font-semibold tabular-nums">
                    {money(proposedPO.dropship_fee)}
                  </dd>
                </div>
              ) : null}
            </dl>
          ) : null}

          {noFreight ? null : (
            <div className="mt-5 border-t pt-4">
              <h4 className="text-sm font-semibold">Shipment decisions</h4>
              <div className="mb-4 mt-2 flex flex-wrap gap-x-4 gap-y-2 text-xs text-muted-foreground">
                <span className="inline-flex items-center gap-1.5">
                  <Truck className="size-3.5" aria-hidden="true" />
                  {needsAccessorialAnswers ? "USA-RS" : "Manufacturer"}
                </span>
                <span className="inline-flex items-center gap-1.5 tabular-nums">
                  <Weight className="size-3.5" aria-hidden="true" />
                  {text(
                    proposedPO.freight_rule_snapshot.estimated_weight_lbs,
                  ) || "-"}{" "}
                  lb
                </span>
                {hasPolicyFreight ? null : (
                  <span className="inline-flex items-center gap-1.5 tabular-nums">
                    <CircleDollarSign className="size-3.5" aria-hidden="true" />
                    {money(proposedPO.freight_surcharge)}
                  </span>
                )}
              </div>
              <div className="grid gap-5 lg:grid-cols-2">
                <div className="min-w-0">
                  <p className="mb-1.5 text-xs font-medium text-foreground">
                    Freight responsibility
                  </p>
                  <ReviewChoiceGroup
                    label="Freight responsibility"
                    value={needsAccessorialAnswers ? "usars" : "manufacturer"}
                    options={FREIGHT_OPTIONS}
                    className="grid w-full grid-cols-2"
                    optionClassName="w-full justify-center"
                    disabled={editsDisabled || update.isPending}
                    onChange={(value) =>
                      update.mutate({
                        proposedPO,
                        changes: {
                          we_book_freight: value === "usars",
                          change_reason: "Freight responsibility updated",
                        },
                      })
                    }
                  />
                </div>
                <div className="min-w-0">
                  <p className="mb-1.5 text-xs font-medium text-foreground">
                    Liftgate
                  </p>
                  <ReviewChoiceGroup
                    label="Liftgate requirement"
                    value={accessorialValue(proposedPO, "liftgate_delivery")}
                    options={DECISION_OPTIONS}
                    className="grid w-full grid-cols-3"
                    optionClassName="w-full justify-center px-2"
                    disabled={editsDisabled || update.isPending}
                    onChange={(value) =>
                      changeAccessorial(
                        {
                          liftgate_delivery:
                            value === "unknown" ? "unknown" : value === "true",
                        },
                        "Liftgate decision updated",
                      )
                    }
                  />
                </div>
              </div>

              <div className="mt-4 grid gap-x-5 gap-y-4 border-t pb-1 pt-4 lg:grid-cols-2">
                {ACCESSORIALS.map(([key, label]) => (
                  <div key={key} className="min-w-0">
                    <p className="mb-1.5 text-xs font-medium text-foreground">
                      {label}
                    </p>
                    <ReviewChoiceGroup
                      label={`${label} requirement`}
                      value={accessorialValue(proposedPO, key)}
                      options={
                        key === "residential_delivery"
                          ? DESTINATION_TYPE_OPTIONS
                          : DECISION_OPTIONS
                      }
                      className="grid w-full grid-cols-3"
                      optionClassName="w-full justify-center px-2"
                      disabled={editsDisabled || update.isPending}
                      onChange={(value) =>
                        changeAccessorial(
                          {
                            [key]:
                              value === "unknown"
                                ? "unknown"
                                : value === "true",
                          },
                          `${label} decision updated`,
                        )
                      }
                    />
                  </div>
                ))}
              </div>
            </div>
          )}

          <section
            id={`po-spec-credit-${proposedPO.uuid}`}
            className={cn("scroll-mt-6", !noFreight && "mt-5 border-t pt-4")}
          >
            <div className="mb-3 flex items-center gap-2">
              <CircleDollarSign
                className="size-4 text-muted-foreground"
                aria-hidden="true"
              />
              <h4 className="text-sm font-semibold">Specification credit</h4>
            </div>
            <ReviewChoiceGroup
              label="Specification credit decision"
              value={specDecision}
              options={specOptions}
              className={cn(
                "grid w-full max-w-2xl grid-cols-1",
                standardRequestAllowed ? "sm:grid-cols-3" : "sm:grid-cols-2",
              )}
              optionClassName="w-full justify-center"
              disabled={editsDisabled || update.isPending || recipientEditing}
              onChange={(value) => changeSpec({ decision: value })}
            />
            {!standardRequestAllowed ? (
              <p className="mt-2 max-w-2xl text-xs leading-5 text-muted-foreground">
                {specCreditPolicyMessage(specPolicy)}
              </p>
            ) : null}
            <div className="mt-3 max-w-xl">
              <DecisionField
                label="Spec credit note"
                value={text(spec.po_note)}
                pending={update.isPending}
                disabled={editsDisabled}
                placeholder="-"
                kind="textarea"
                onEditingChange={(editing) =>
                  trackEditing("spec-credit-note", editing)
                }
                onSave={(value) => changeSpec({ po_note: value })}
              />
            </div>
          </section>
        </section>

        <SupplierInstructionsPanel
          proposedPO={proposedPO}
          pending={update.isPending}
          disabled={editsDisabled}
          onEditingChange={trackEditing}
          onSavePOInstructions={(value) =>
            update.mutate({
              proposedPO,
              changes: {
                po_instructions: value.trim() || null,
                change_reason: "PO instructions updated",
              },
            })
          }
          onSaveShippingInstructions={(value) =>
            changeAccessorial(
              { instructions: value.trim() || null },
              "Shipping instructions updated",
            )
          }
        />

        <section
          id={`po-lines-${proposedPO.uuid}`}
          aria-label="Supplier PO document"
          className="scroll-mt-6 overflow-hidden rounded-xl border"
        >
          <div className="flex flex-wrap items-center justify-between gap-3 border-b bg-muted/20 px-4 py-3">
            <div
              role="group"
              aria-label="Supplier PO view"
              className="inline-grid grid-cols-2 rounded-lg border bg-muted/40 p-1"
            >
              <button
                type="button"
                aria-pressed={artifactView === "lines"}
                onClick={() => setArtifactView("lines")}
                className={cn(
                  "inline-flex min-h-8 items-center justify-center gap-2 rounded-md px-3 text-xs font-medium text-muted-foreground transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring/50",
                  artifactView === "lines" &&
                    "bg-background text-foreground shadow-sm",
                )}
              >
                <List className="size-3.5" aria-hidden="true" />
                Line items
              </button>
              <button
                type="button"
                aria-pressed={artifactView === "pdf"}
                onClick={() => setArtifactView("pdf")}
                className={cn(
                  "inline-flex min-h-8 items-center justify-center gap-2 rounded-md px-3 text-xs font-medium text-muted-foreground transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring/50",
                  artifactView === "pdf" &&
                    "bg-background text-foreground shadow-sm",
                )}
              >
                <FileText className="size-3.5" aria-hidden="true" />
                Supplier PDF
              </button>
            </div>
            {artifactView === "lines" ? (
              <Badge variant="outline">
                {proposedPO.lines.length} line
                {proposedPO.lines.length === 1 ? "" : "s"}
              </Badge>
            ) : null}
          </div>
          {artifactView === "lines" ? (
            <DropshipProposedPOLineTable
              draftUuid={draftUuid}
              proposedPO={proposedPO}
              disabled={editsDisabled}
              framed={false}
              onEditingChange={trackEditing}
              titleId={`proposed-po-lines-${proposedPO.uuid}`}
            />
          ) : (
            <SupplierPDFPreview
              draftUuid={draftUuid}
              proposedPO={proposedPO}
              enabled
            />
          )}
        </section>
      </div>

      <div className="xl:sticky xl:top-6">
        <ReviewApprovalPanel
          title={
            hasSalesDivergenceBlocker
              ? "Decision needed"
              : `PO ${proposedPO.display_order} approval`
          }
          titleId={`po-approval-${proposedPO.uuid}`}
          checks={checks}
          blockingCount={blockingCount}
          showBlockingCount={!hasSalesDivergenceBlocker}
          showCheckSections={!hasSalesDivergenceBlocker}
          totals={[
            {
              label: "Supplier PO total",
              value: money(proposedPO.total),
              emphasized: true,
            },
          ]}
          totalsNote=""
          note={approvalNote}
          notePlaceholder="Approval note (optional)"
          onNoteChange={
            !alreadyFinal && !hasSalesDivergenceBlocker
              ? setApprovalNote
              : undefined
          }
          primaryLabel={
            hasSalesDivergenceBlocker
              ? "Approve change"
              : proposedPO.state === "approved"
                ? "PO approved"
                : "Approve PO"
          }
          primaryDisabled={
            hasSalesDivergenceBlocker ? overrideDisabled : approveDisabled
          }
          primaryPending={approve.isPending}
          primaryHint={
            !hasSalesDivergenceBlocker && blockingCount
              ? "Complete each blocking decision before approval."
              : undefined
          }
          footer={
            !alreadyFinal ? (
              <div className="grid gap-2">
                <ActionPopover
                  trigger={
                    <Button
                      type="button"
                      variant="outline"
                      className="w-full whitespace-normal"
                      disabled={
                        !canApprove ||
                        hasEditingFields ||
                        update.isPending ||
                        approve.isPending ||
                        rejectToSales.isPending
                      }
                    >
                      Reopen quote
                    </Button>
                  }
                  title="Reopen the quote?"
                  note={{
                    label: "What needs to change?",
                    placeholder:
                      "Example: Reprice freight for construction-site delivery.",
                    minLength: 1,
                  }}
                  confirmLabel="Reopen quote"
                  pending={rejectToSales.isPending}
                  pendingLabel="Reopening quote..."
                  canConfirm={canApprove && !hasEditingFields}
                  onConfirm={(reason, close) =>
                    rejectToSales.mutate(
                      { proposedPO, reason },
                      { onSuccess: close },
                    )
                  }
                >
                  <p className="text-xs text-muted-foreground">
                    Update the quote or freight, then approve Sales review again
                    to rebuild the POs.
                  </p>
                </ActionPopover>
              </div>
            ) : undefined
          }
          wrapPrimary={(button) =>
            hasSalesDivergenceBlocker && !alreadyFinal ? (
              <ActionPopover
                anchorOnly
                open={overrideDialogOpen}
                onOpenChange={setOverrideDialogOpen}
                trigger={button}
                title="Approve this change?"
                note={{
                  label: "Why is this okay?",
                  placeholder:
                    "Example: Customer confirmed construction-site delivery.",
                  minLength: 1,
                }}
                confirmLabel="Approve change"
                pending={approve.isPending}
                pendingLabel="Approving..."
                canConfirm={!overrideDisabled}
                onConfirm={(reason, close) =>
                  approve.mutate(
                    {
                      proposedPO,
                      note:
                        approvalNote.trim() ||
                        "PO approved with a freight override.",
                      operationsOverrideReason: reason,
                    },
                    {
                      onSuccess: () => {
                        close();
                        onApproved?.();
                      },
                      onError: (error) =>
                        toast.error("Freight override was blocked", {
                          description: dropshipV2ErrorMessage(
                            error,
                            "Review the current PO blockers and try again.",
                          ),
                        }),
                    },
                  )
                }
              >
                <p className="text-xs text-muted-foreground">
                  Keep the Sales price and use the current delivery settings.
                </p>
              </ActionPopover>
            ) : (
              button
            )
          }
          onPrimaryAction={() => {
            if (hasSalesDivergenceBlocker) {
              setOverrideDialogOpen(true);
              return;
            }
            approve.mutate(
              {
                proposedPO,
                note:
                  approvalNote.trim() ||
                  "PO, accessorials, spec credit, and supplier PDF reviewed.",
              },
              {
                onSuccess: () => onApproved?.(),
                onError: (error) =>
                  toast.error("PO approval blocked", {
                    description: dropshipV2ErrorMessage(
                      error,
                      "Review the unresolved PO decisions.",
                    ),
                  }),
              },
            );
          }}
        />
      </div>
    </div>
  );
}
